import React, { useState, useMemo, useEffect } from 'react'
import RosterGrid from './RosterGrid'
import SidePanel from './SidePanel'
import FormationModal from './FormationModal'
import SiegePanel from './SiegePanel'
import CommandersPanel from './CommandersPanel'
import { formatLogEntry, formatScoutAudit } from '../logFormat'
import { C, ui, muteColor } from '../theme'

const SCOUT_TYPES = [['nimble', 'Nimble'], ['lightHorse', 'Light horse'], ['flying', 'Flying'], ['caster', 'Caster']]
const blankParties = () => ({ short: { nimble: 0, lightHorse: 0, flying: 0, caster: 0 }, long: { nimble: 0, lightHorse: 0, flying: 0, caster: 0 } })
// scout-capable men available in an army, by party type (mirrors the engine's role-tag mapping)
const scoutAvail = (army) => {
  const tag = { nimble: 'nimble', lightHorse: 'light_horse', flying: 'flying', caster: 'caster' }
  const r = { nimble: 0, lightHorse: 0, flying: 0, caster: 0 }
  for (const p of (army.pools || [])) {
    if (p.disbanded) continue
    const tags = String(p.role_tags || '').split(',').map(s => s.trim())
    for (const k of Object.keys(tag)) if (tags.includes(tag[k])) r[k] += p.men || 0
  }
  return r
}
// scout-capable men currently DETACHED AS SCOUTS (formation Scouts bucket), by party type —
// this is what actually feeds scout parties since the marching-formation feature.
const scoutBucketAvail = (army) => {
  const tag = { nimble: 'nimble', lightHorse: 'light_horse', flying: 'flying', caster: 'caster' }
  const byPool = (army.formation && army.formation.byPool) || {}
  const r = { nimble: 0, lightHorse: 0, flying: 0, caster: 0 }
  for (const p of (army.pools || [])) {
    if (p.disbanded) continue
    const scouts = (byPool[String(p.id)] || {}).scouts || 0
    if (!scouts) continue
    const tags = String(p.role_tags || '').split(',').map(s => s.trim())
    for (const k of Object.keys(tag)) if (tags.includes(tag[k])) r[k] += scouts
  }
  return r
}
// formation bucket labels/order for the summary card (mirrors src/engine/formation.js)
const FORMATION_ORDER = [['main', 'Main body'], ['vanguard', 'Vanguard'], ['rear', 'Rear guard'], ['scouts', 'Scouts'], ['supply_line', 'Supply-line guard'], ['raiders', 'Raiders']]

// supply-in-days formatting: one decimal under 10 days, whole above
const fmtD = (d) => d == null ? '—' : (d >= 10 ? String(Math.round(d)) : String(Math.round(d * 10) / 10))

// Temporary detachments (Rules: "Movement"). Eligible = every live pool is cavalry (mounted)
// or flying — no infantry. Camp followers, once abandoned, are "safe" for 15 days before the
// units would take a −2 (we only alert). A detachment is flagged by detachment_since_day.
const CAMP_FOLLOWER_MAX_DAYS = 15
const poolTags = (p) => String(p.role_tags || '').split(',').map(s => s.trim())
const detachEligible = (army) => {
  const live = (army.pools || []).filter(p => !p.disbanded)
  return live.length > 0 && live.every(p => poolTags(p).includes('mounted') || poolTags(p).includes('flying'))
}

// Where a placed entity sits: the settlement's name when it's on a named city tile,
// else the (row,col) grid reference. `o` carries tile_name/tile_row/tile_col.
const placeOf = (o, { prefix = '' } = {}) => {
  if (o.tile_row == null) return 'unplaced'
  if (o.tile_name) return `${prefix}${o.tile_name}`
  return `${prefix}(${o.tile_row},${o.tile_col})`
}
// A supply base's site: its city name when named, else the City/Fort kind + grid ref.
const baseLabel = (b) => b.tile_name || `${b.is_city ? 'City' : 'Fort'} (${b.tile_row},${b.tile_col})`

// ETA-aware supply reading for an army. 'low' = under LOW_DAYS carried with no caravan due
// in time (the real "you're about to have problems" state); 'inbound' = a caravan arrives
// before the larder empties, so the low state is already handled.
const LOW_DAYS = 3
// half rations stretch the same stock twice as far
const effDays = (a) => a.carried_days == null ? null : a.carried_days * (a.half_rations ? 2 : 1)
const supplyStatus = (a, caravans = []) => {
  if (a.fog || a.carried_days == null) return null
  const days = effDays(a)
  const etas = caravans.filter(c => c.target_army_id === a.id && c.eta_days != null).map(c => c.eta_days)
  const eta = etas.length ? Math.min(...etas) : null
  if (days >= LOW_DAYS) return eta != null ? { kind: 'ok-inbound', eta } : null
  if (eta != null && eta <= Math.max(days, 1)) return { kind: 'inbound', eta }
  return { kind: 'low', eta }
}
// pools that are still with us — dead pools keep their starvation counters forever and
// must not trip warnings (the "under-supplied ghost" bug)
const livePools = (a) => (a.pools || []).filter(p => !p.disbanded)

// one-line summary of fogged enemy intel
const intelLine = (fog) => {
  if (!fog) return ''
  if (fog.certainty === 'rumor') {
    const bits = [`rumored ${fog.sizeBucket || ''} force`.trim()]
    if (fog.errorAreas) bits.push(`±${fog.errorAreas * 6}mi`)
    if (fog.moving && fog.direction) bits.push(`moving ${fog.direction}`)
    bits.push(`day ${fog.knownDay}`)
    return bits.join(' · ')
  }
  const bits = []
  if (fog.sizeEstimate != null) bits.push(`~${fog.sizeEstimate} men${fog.sizeError ? ` (±${fog.sizeError})` : ''}`)
  bits.push(fog.certainty === 'dm' ? 'DM-revealed' : 'scouted')
  bits.push(`day ${fog.knownDay}`)
  return bits.join(' · ')
}

const h = ui.h
const input = ui.input
const btn = ui.btn
const btnGhost = ui.btnGhost
const row = ui.row

// Segmented tab bar across the top of the panel. Tabs are [key, label] pairs.
function TabBar({ tabs, active, onPick }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {tabs.map(([key, label]) => (
        <button key={key} onClick={() => onPick(key)}
          style={{
            flex: '1 1 auto', padding: '6px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
            fontFamily: "'Cinzel', serif", letterSpacing: 0.3, whiteSpace: 'nowrap',
            border: `1px solid ${active === key ? C.gold : C.rule}`,
            background: active === key ? C.gold : C.paperRaised,
            color: active === key ? C.paperRaised : C.ink,
          }}>{label}</button>
      ))}
    </div>
  )
}

export default function ArmyPanel({
  isDm, canAct, factions, myFactionId, armies, selectedArmyId, mode, plannedPath, plannedCost = 0, currentDay,
  preview, supplyBases = [], caravans = [], campFollowers = [], troopTypes = [],
  onSelectArmy, onDeselect, onSetRoster, onIssueOrder, onClearPath, onPreview, onCommit, onSetSupply,
  onRecruit, onRaid, onStandDown, onProvision, onResupplyBase, onDispatchCaravan,
  onPreviewDispatch, onPatchBase, onSetAutoResupply, onSetDetachment, onDisperseFollowers,
  onSetScouting, onSetFormation, onReveal, scoutEvents, scoutAudit,
  // commanders (roster tab + leaderless warnings)
  commandersData, onCreateCommander, onPatchCommander, onDeleteCommander,
  strongholds = [], sieges = [], onBesiege, onLiftSiege, onAssault, onSally, onSiegeTerms,
  onResolveSiege, onSurrenderCheck, onSiegeAllocation, onSpecialists, onPatchStronghold, onSeedStrongholds,
  // identity/members — rendered in the "Campaign" world tab (folded in from SidePanel)
  user, members = [], myMembership, onIdentify, onJoin, pendingJoinCode,
}) {
  const [factionId, setFactionId] = useState(myFactionId || (factions[0] && factions[0].id))
  const [supplyGp, setSupplyGp] = useState(1000)
  const [provisionDays, setProvisionDays] = useState(5)
  const [stance, setStance] = useState('foraging')
  const [logiGp, setLogiGp] = useState(1000)
  const [baseTargetDraft, setBaseTargetDraft] = useState({}) // baseId → target_days being edited
  const [dispatchBaseId, setDispatchBaseId] = useState(null)
  const [dispatchGp, setDispatchGp] = useState(0)
  const [dispatchPreview, setDispatchPreview] = useState(null)
  const [resupplyDraft, setResupplyDraft] = useState(null) // selected army's resupply_target_days edit
  const [reserve, setReserve] = useState({ men: 25, wagePerMan: 1, troopCode: '' })
  const [scoutForm, setScoutForm] = useState(null)
  const [formationOpen, setFormationOpen] = useState(false)
  const [worldTab, setWorldTab] = useState('armies') // Armies | Logistics | Day | Campaign
  const [armyTab, setArmyTab] = useState('roster')    // Roster | Supply | Orders | Formation | Scouting
  // Keep an identified-but-not-yet-joined user on the Campaign tab so identifying doesn't bounce
  // them off the join panel before they've actually joined (the tab is only clamped there while
  // signed out). Once they join — or if they navigate away — this stops forcing.
  useEffect(() => { if (user && !myMembership) setWorldTab('campaign') }, [user, myMembership])
  const selected = armies.find(a => a.id === selectedArmyId)
  const ownSelected = selected && !selected.fog && (isDm || selected.faction_id === myFactionId)
  const armyContext = !!(selected && !selected.fog) // a commandable army is selected → army tabs

  // (re)initialize the scouting form from the selected own army's saved config
  useEffect(() => {
    if (!ownSelected) { setScoutForm(null); return }
    let cfg = {}; try { cfg = JSON.parse(selected.scouting_json || '{}') || {} } catch { cfg = {} }
    const p = blankParties()
    setScoutForm({
      shortRangeMi: cfg.shortRangeMi || 0, longRangeMi: cfg.longRangeMi || 0,
      parties: { short: { ...p.short, ...(cfg.parties && cfg.parties.short) }, long: { ...p.long, ...(cfg.parties && cfg.parties.long) } },
      halfSpeed: !!cfg.halfSpeed, localGuide: !!cfg.localGuide, rangeLockedUntilDay: cfg.rangeLockedUntilDay || 0,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArmyId, ownSelected, selected && selected.scouting_json])

  // Default the dispatch base to the nearest active one when the selection changes (taxicab
  // heuristic — the server preview then reports the real route numbers). Keyed on the army,
  // not on supplyBases, so a background poll doesn't clobber the user's pick.
  // close the formation editor when the selection changes (it's bound to one army)
  useEffect(() => { setFormationOpen(false) }, [selectedArmyId])

  useEffect(() => {
    setDispatchPreview(null)
    setResupplyDraft(null)
    if (!ownSelected || selected.tile_row == null) { setDispatchBaseId(null); return }
    const usable = supplyBases.filter(sb => sb.faction_id === selected.faction_id && sb.active && sb.tile_row != null)
    if (!usable.length) { setDispatchBaseId(null); return }
    const dist = (sb) => Math.abs(sb.tile_row - selected.tile_row) + Math.abs(sb.tile_col - selected.tile_col)
    const stocked = usable.filter(sb => sb.stored_supply_gp > 0)
    const pool = stocked.length ? stocked : usable
    setDispatchBaseId(pool.reduce((m, sb) => (dist(sb) < dist(m) ? sb : m), pool[0]).id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArmyId, ownSelected])

  // Live dispatch preview: route days, surcharge and a recommended load for the picked base.
  useEffect(() => {
    if (!ownSelected || !canAct || dispatchBaseId == null || !onPreviewDispatch) return
    let stale = false
    onPreviewDispatch(dispatchBaseId, selected.id).then(p => {
      if (stale) return
      setDispatchPreview(p)
      if (p && p.reachable && p.recommendedGp != null) setDispatchGp(p.recommendedGp)
    })
    return () => { stale = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchBaseId, selectedArmyId, ownSelected, canAct, selected && selected.tile_id, selected && Math.round(selected.carried_supply_gp || 0)])

  // id→name lookups so the day-preview log reads as sentences instead of raw JSON
  const logCtx = useMemo(() => {
    const armyById = new Map(armies.map(a => [a.id, a]))
    const factionById = new Map(factions.map(f => [f.id, f]))
    const poolById = new Map()
    for (const a of armies) for (const p of (a.pools || [])) poolById.set(p.id, { troop_name: p.troop_name, army_name: a.name })
    return { armyById: (id) => armyById.get(id), factionById: (id) => factionById.get(id), poolById: (id) => poolById.get(id) }
  }, [armies, factions])

  // faction the logistics controls act for: DM picks; a player uses their own.
  const logiFactionId = isDm ? factionId : myFactionId
  const myBases = supplyBases.filter(sb => sb.faction_id === logiFactionId)

  const submitReinforce = async () => {
    if (!selected) return
    const pool = selected.pools.find(p => p.troop_code === reserve.troopCode)
    await onRecruit({
      factionId: selected.faction_id, mode: 'reinforce', targetArmyId: selected.id,
      troopCode: reserve.troopCode, men: Number(reserve.men), wagePerMan: pool ? pool.wage_per_man : Number(reserve.wagePerMan),
    })
  }

  // ── tab sets (depend on role + selection) ───────────────────────────────────
  const worldTabs = !user
    ? [['campaign', 'Campaign']]
    : [
        ['armies', 'Armies'],
        ...(canAct ? [['commanders', 'Commanders'], ['logistics', 'Logistics'], ['strongholds', 'Strongholds']] : []),
        ...(isDm ? [['day', 'Day']] : []),
        ['campaign', 'Campaign'],
      ]
  // Siege tab: only where there is actually a stronghold under this army's feet.
  const strongholdHere = armyContext
    ? strongholds.find(s => s.row === selected.tile_row && s.col === selected.tile_col)
    : null
  const siegeHere = strongholdHere
    ? sieges.find(s => s.stronghold_id === strongholdHere.id && s.status === 'active')
    : null
  const armyTabs = [
    ['roster', 'Roster'],
    ...(canAct ? [['supply', 'Supply'], ['orders', 'Orders']] : []),
    ...((ownSelected && strongholdHere) ? [['siege', siegeHere ? 'Siege ⚔' : 'Siege']] : []),
    ...((ownSelected && selected.formation) ? [['formation', 'Formation']] : []),
    ...((ownSelected && scoutForm) ? [['scouting', 'Scouting']] : []),
  ]
  // clamp active tab to what's actually available this render (no effect needed)
  const activeWorld = worldTabs.some(t => t[0] === worldTab) ? worldTab : worldTabs[0][0]
  const activeArmy = armyTabs.some(t => t[0] === armyTab) ? armyTab : armyTabs[0][0]

  // ── world-tab bodies ────────────────────────────────────────────────────────
  const renderDay = () => (
    <>
      <div style={h}>Day {currentDay} — Engine</div>
      <div style={row}>
        <button style={btnGhost} onClick={onPreview}>Preview day</button>
        <button style={btn} onClick={onCommit}>Advance & commit ▶</button>
      </div>
      {preview && (
        <div style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto', fontSize: 12.5, background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 6, padding: 8 }}>
          <div style={{ opacity: 0.6, marginBottom: 4 }}>Preview log ({preview.length})</div>
          {preview.length === 0 && <div style={{ opacity: 0.5 }}>Nothing happens this day.</div>}
          {preview.map((e, i) => (
            <div key={i} style={{ padding: '1px 0' }}>{formatLogEntry(e, logCtx)}</div>
          ))}
        </div>
      )}
      {scoutEvents && scoutEvents.length > 0 && (
        <div style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto', fontSize: 12.5, background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 6, padding: 8 }}>
          <div style={{ opacity: 0.6, marginBottom: 4 }}>Scouting (preview, {scoutEvents.length})</div>
          {scoutEvents.map((e, i) => {
            const f = logCtx.factionById(e.payload.faction)
            const by = logCtx.armyById(e.payload.byArmy)
            const tgt = logCtx.armyById(e.payload.target)
            const detect = e.type === 'scouting_detect'
            return (
              <div key={i} style={{ padding: '2px 0', borderTop: i ? `1px solid ${C.rule}` : 'none' }}>
                <span style={{ color: detect ? C.ink : C.gold }}>{detect ? '✦ detected' : '⚔ contact'}</span>
                {` · ${f ? f.name : 'F' + e.payload.faction}'s ${by ? by.name : '#' + e.payload.byArmy}`}
                {` ${detect ? 'spotted' : 'met'} ${tgt ? tgt.name : '#' + e.payload.target}`}
                {` (${e.payload.range}${e.payload.ambush ? ', ambush risk' : ''})`}
                {!detect && onReveal && (
                  <button style={{ ...btnGhost, padding: '1px 6px', marginLeft: 6, fontSize: 11 }}
                    onClick={() => onReveal(e.payload.faction, e.payload.target, true)}>Reveal</button>
                )}
              </div>
            )
          })}
        </div>
      )}
      {scoutAudit && scoutAudit.length > 0 && (
        <div style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto', fontSize: 12, background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 6, padding: 8 }}>
          <div style={{ opacity: 0.6, marginBottom: 4 }}>Scouting audit (preview, {scoutAudit.length}) — why each army did/didn't roll</div>
          {scoutAudit.map((a, i) => {
            const dim = a.kind === 'skip' || a.kind === 'no-target' || a.outcome === 'miss'
            const hit = a.outcome === 'detect'
            return (
              <div key={i} style={{ padding: '1px 0', color: hit ? C.green : dim ? C.inkFaint : C.ink }}>
                {formatScoutAudit({ payload: a }, logCtx)}
              </div>
            )
          })}
        </div>
      )}
    </>
  )

  const renderLogistics = () => {
    const sorted = myBases.slice().sort((a, b) => (b.active - a.active) || (b.stored_supply_gp - a.stored_supply_gp))
    const commitBaseTarget = (b) => {
      const draft = baseTargetDraft[b.id]
      setBaseTargetDraft(d => { const n = { ...d }; delete n[b.id]; return n })
      if (draft == null) return
      const v = Math.max(0, Number(draft) || 0)
      if (v !== b.target_days) onPatchBase(b.id, { target_days: v })
    }
    return (
    <>
      <div style={h}>Supply bases ({sorted.filter(b => b.active).length} active)</div>
      {isDm && factions.length > 1 && (
        <select style={input} value={factionId} onChange={e => setFactionId(Number(e.target.value))}>
          {factions.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      )}
      <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6 }}>
        Controlled cities & forts are bases by default. Each dawn a base stocks itself from the treasury toward its
        target — that many days of supply for every army (garrison included) drawing from it.
      </div>
      {sorted.length === 0 && <div style={{ opacity: 0.6, fontSize: 12 }}>No bases — control a city or fort.</div>}
      {sorted.map(b => (
        <div key={b.id} style={{ marginTop: 6, padding: 8, background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 6, opacity: b.active ? 1 : 0.6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: b.active ? 5 : 0 }}>
            <b style={{ flex: 1 }}>{baseLabel(b)} — {Math.round(b.stored_supply_gp).toLocaleString()} gp</b>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!b.active} onChange={e => onPatchBase(b.id, { active: e.target.checked })} />
              active
            </label>
          </div>
          {b.active ? (
            <>
              <div style={{ ...row, alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 11 }}>Auto-stock target</span>
                <input style={{ ...input, width: 58, marginBottom: 0 }} type="number" min={0}
                  value={baseTargetDraft[b.id] ?? b.target_days ?? 30}
                  onChange={e => setBaseTargetDraft(d => ({ ...d, [b.id]: e.target.value }))}
                  onBlur={() => commitBaseTarget(b)} onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }} />
                <span style={{ fontSize: 11 }}>days {Number(baseTargetDraft[b.id] ?? b.target_days) === 0 ? '(off)' : ''}</span>
              </div>
              <div style={row}>
                <input style={{ ...input, flex: 1, marginBottom: 0 }} type="number" value={logiGp} onChange={e => setLogiGp(Number(e.target.value))} />
                <button style={btnGhost} onClick={() => onResupplyBase(b.id, logiGp)}>+ Stock</button>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 11, opacity: 0.7 }}>inactive — holds no stock and armies won't draw from it</div>
          )}
        </div>
      ))}
      {sorted.length > 0 && <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6 }}>Closing a stocked base after day 1 sends its stores to the nearest base by caravan. Manual dispatches live on an army’s <b>Supply</b> tab.</div>}
      {(() => {
        const own = caravans.filter(c => !c.fog)
        return own.length > 0 && (
          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
            {own.length} caravan(s) en route: {own.map(c => `→ ${c.target_army_name || (c.target_base_id ? 'base' : '?')}${c.eta_days != null ? ` ~${c.eta_days}d` : ''}`).join(', ')}
          </div>
        )
      })()}
    </>
    )
  }

  const renderArmiesList = () => (
    <>
      <div style={h}>Armies ({armies.length})</div>
      {!canAct && <div style={{ opacity: 0.6, fontSize: 13, marginBottom: 8 }}>Join a faction to command armies — see the Campaign tab.</div>}
      {armies.length === 0 && <div style={{ opacity: 0.5, fontSize: 13 }}>No armies visible.</div>}
      {armies.map(a => (
        <div key={a.id}
          onClick={() => onSelectArmy(a.id)}
          style={{
            padding: '7px 8px', borderRadius: 6, marginBottom: 4, cursor: 'pointer',
            background: a.id === selectedArmyId ? C.highlight : C.paperRaised,
            border: `1px solid ${a.id === selectedArmyId ? C.gold : C.rule}`,
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ width: 10, height: 10, borderRadius: a.fog ? 2 : '50%', background: muteColor(a.faction_color), display: 'inline-block', opacity: a.fog ? 0.7 : 1 }} />
            <b style={{ flex: 1, fontStyle: a.fog ? 'italic' : 'normal' }}>{a.fog ? `${a.fog.certainty === 'rumor' ? '?' : '✦'} ${a.name}` : a.name}</b>
            <span style={{ fontSize: 11, opacity: 0.7 }}>{placeOf(a, { prefix: a.fog && a.fog.approx ? '~' : '' })}</span>
          </div>
          {a.fog ? (
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 3, fontStyle: 'italic' }}>
              {intelLine(a.fog)}
            </div>
          ) : (
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 3 }}>
            {a.pools.reduce((s, p) => s + (p.disbanded ? 0 : p.men), 0)} men · {a.faction_name}
            {a.commander_name ? ` · ${a.commander_name}` : ''}
            {!a.commanded && !a.leaderless_exempt && (isDm || a.faction_id === myFactionId)
              ? <span style={{ color: C.gold }}> · ⚠ no commander{a.inbound_commander_name ? ` (${a.inbound_commander_name} arrives day ${a.inbound_commander_day})` : ''}</span>
              : ''}
            {` · ${fmtD(a.carried_days)}d supply`}
            {a.half_rations ? ' · ½ rations' : ''}
            {a.detachment_since_day != null ? ' · detachment' : ''}
            {a.pendingOrder && a.pendingOrder.order_type === 'raid' ? ` · raiding (${a.pendingOrder.payload.stance})` : ''}
            {a.pendingOrder && a.pendingOrder.order_type === 'move' ? ` · moving (${a.pendingOrder.payload.path.length} left)` : ''}
            {livePools(a).some(p => p.unsupplied_days > 0) ? <span style={{ color: C.gold }}> · ⚠ under-supplied</span> : ''}
            {(() => {
              const st = supplyStatus(a, caravans)
              if (st && st.kind === 'low') return <span style={{ color: C.gold }}> · ⚠ low supply</span>
              if (st && st.kind === 'inbound') return <span> · ⛟ resupply ~{st.eta}d</span>
              return ''
            })()}
          </div>
          )}
        </div>
      ))}
    </>
  )

  const renderCampaign = () => (
    <SidePanel user={user} members={members} factions={factions} myMembership={myMembership} onIdentify={onIdentify} onJoin={onJoin} pendingJoinCode={pendingJoinCode} />
  )

  // fogged enemy: no commands, just an intel card (shown above world tabs)
  const renderFoggedCard = () => (
    <div style={{ marginBottom: 10, padding: 10, background: C.paperSunken, border: `1px dashed ${C.rule}`, borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <b style={{ flex: 1, fontStyle: 'italic' }}>{selected.name}</b>
        <button style={{ ...btnGhost, padding: '3px 8px' }} onClick={onDeselect}>✕</button>
      </div>
      <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>{intelLine(selected.fog)}</div>
      <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
        {selected.fog.certainty === 'rumor' ? 'Hearsay — position approximate, composition unknown.' : 'Detected — exact composition not yet known.'}
      </div>
    </div>
  )

  // ── army-tab bodies ─────────────────────────────────────────────────────────
  const renderRoster = () => (
    <>
      <div style={h}>Roster {isDm ? '— edit counts (DM)' : ''}</div>
      {isDm ? (
        <RosterGrid catalog={troopTypes} army={selected} canAct={canAct} isDm={isDm}
          onSave={(rows, charge) => onSetRoster(selected.id, rows, charge)} />
      ) : (
        <RosterGrid catalog={troopTypes} army={selected} canAct={false} isDm={false} onSave={() => {}} />
      )}
      {canAct && selected.pools.length > 0 && (
        <>
          <div style={h}>Reinforce w/ reserves</div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6 }}>Half gold, no training; ≤¼ of the unit.</div>
          <div style={row}>
            <select style={{ ...input, flex: 2 }} value={reserve.troopCode} onChange={e => setReserve({ ...reserve, troopCode: e.target.value })}>
              <option value="">pool…</option>
              {selected.pools.filter(p => !p.disbanded).map(p => (
                <option key={p.id} value={p.troop_code}>{p.troop_name} ({p.troop_code})</option>
              ))}
            </select>
            <input style={{ ...input, flex: 1 }} type="number" placeholder="men" value={reserve.men} onChange={e => setReserve({ ...reserve, men: Number(e.target.value) })} />
          </div>
          <button style={btnGhost} disabled={!reserve.troopCode || !reserve.men} onClick={submitReinforce}>Add reserves</button>
        </>
      )}
    </>
  )

  const renderSupply = () => {
    const need = selected.daily_need_gp || 0
    const st = supplyStatus(selected, caravans)
    const armyBases = supplyBases.filter(sb => sb.faction_id === selected.faction_id && sb.active)
    const targetDays = resupplyDraft != null ? resupplyDraft : (selected.resupply_target_days ?? 5)
    const commitTargetDays = () => {
      const v = Math.max(0, Number(targetDays) || 0)
      setResupplyDraft(null)
      if (v !== (selected.resupply_target_days ?? 5)) onSetAutoResupply(selected.id, { resupply_target_days: v })
    }
    // holding beyond the free allowance feeds its own train (flyers get grounded instead)
    const allFlying = selected.pools.length > 0 && selected.pools.every(p => p.disbanded || String(p.role_tags || '').split(',').map(s => s.trim()).includes('flying'))
    const freeDays = allFlying ? 2 : 5
    const wastePerDay = (!allFlying && selected.carried_days > 5 && need > 0)
      ? Math.min(need * (Math.pow(1.1, selected.carried_days - 5) - 1), 0.25 * (selected.carried_days - 5) * need)
      : 0
    const groundedByBaggage = allFlying && selected.carried_days != null && selected.carried_days > 2
    return (
    <>
      <div style={h}>Supply</div>
      <div style={{ fontSize: 12, marginBottom: 2 }}>
        Carried: <b>{fmtD(selected.carried_days)} days</b>
        {selected.half_rations ? <b> (≈{fmtD(effDays(selected))} on ½ rations)</b> : null}
        <span style={{ opacity: 0.7 }}> ({Math.round(selected.carried_supply_gp || 0).toLocaleString()} gp · needs {Math.round(need).toLocaleString()} gp/day)</span>
      </div>
      {livePools(selected).some(p => p.unsupplied_days > 0) && (
        selected.half_rations
          ? <div style={{ fontSize: 12, color: C.gold }}>½ rations — held at the “no supplies today” penalty; the recovery debt grows each day</div>
          : <div style={{ fontSize: 12, color: C.gold }}>⚠ under-supplied — penalties accruing</div>
      )}
      {wastePerDay > 0.5 && <div style={{ fontSize: 12, color: C.gold }}>⚠ over-laden — the excess train eats ~{Math.round(wastePerDay)} gp/day of stock (waste starts above 5 days)</div>}
      {groundedByBaggage && <div style={{ fontSize: 12, color: C.gold }}>⚠ grounded by baggage — above 2 days carried this force marches (speed 2, terrain costs apply)</div>}
      {st && st.kind === 'low' && <div style={{ fontSize: 12, color: C.gold }}>⚠ low supply{st.eta != null ? ` — a caravan is ~${st.eta}d out and may not arrive in time` : ' — no caravan inbound'}</div>}
      {st && st.kind === 'inbound' && <div style={{ fontSize: 12, opacity: 0.8 }}>⛟ resupply inbound (~{st.eta}d)</div>}
      {st && st.kind === 'ok-inbound' && <div style={{ fontSize: 12, opacity: 0.6 }}>⛟ caravan inbound (~{st.eta}d)</div>}
      {(() => {
        // supply-line guards (Formation bucket) ride the inbound caravans — flavor note
        const guarded = caravans.filter(c => !c.fog && c.target_army_id === selected.id && c.guards && c.guards.length)
        return guarded.map(c => (
          <div key={c.id} style={{ fontSize: 11, opacity: 0.7 }}>
            ⛨ caravan{c.eta_days != null ? ` (~${c.eta_days}d)` : ''} guarded by {c.guards.map(g => `${g.men} ${g.troop_name}`).join(', ')}
          </div>
        ))
      })()}

      <div style={{ ...row, marginTop: 6, alignItems: 'center' }}>
        <input style={{ ...input, flex: 1, marginBottom: 0 }} type="number" value={supplyGp} onChange={e => setSupplyGp(Number(e.target.value))} />
        <span style={{ fontSize: 11, opacity: 0.6, whiteSpace: 'nowrap' }}>≈ {need > 0 ? fmtD(supplyGp / need) : '—'}d</span>
        <button style={btnGhost} onClick={() => onSetSupply(selected.id, supplyGp)}>Set carried</button>
      </div>
      <div style={{ fontSize: 11, opacity: 0.6, margin: '4px 0' }}>
        Provision from treasury at face value — but stock beyond {freeDays} days is eaten by its own supply train each day{allFlying ? ' (flyers instead lose flight above 2 days)' : ''}. New armies start provisioned automatically.
      </div>
      <div style={row}>
        <input style={{ ...input, flex: 1 }} type="number" min={0} value={provisionDays} onChange={e => setProvisionDays(Number(e.target.value))} />
        <button style={btnGhost} onClick={() => onProvision(selected.id, provisionDays)}>Provision {provisionDays}d</button>
      </div>
      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
        <input type="checkbox" checked={!!selected.half_rations}
          onChange={e => onSetAutoResupply(selected.id, { half_rations: e.target.checked ? 1 : 0 })} />
        Half rations
      </label>
      <div style={{ fontSize: 11, opacity: 0.6, margin: '2px 0 4px' }}>
        Stretches carried supplies ×2. The men sit at the “no supplies today” penalty (never worse) while the debt to recover from it keeps growing.
      </div>

      <div style={h}>Auto-resupply</div>
      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="checkbox" checked={!!selected.auto_resupply}
          onChange={e => onSetAutoResupply(selected.id, { auto_resupply: e.target.checked ? 1 : 0 })} />
        Keep supplied from the nearest base
      </label>
      {!!selected.auto_resupply && (
        <div style={{ ...row, marginTop: 4, alignItems: 'center' }}>
          <span style={{ fontSize: 12 }}>Top up to</span>
          <input style={{ ...input, width: 64, marginBottom: 0 }} type="number" min={0} value={targetDays}
            onChange={e => setResupplyDraft(e.target.value)} onBlur={commitTargetDays}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }} />
          <span style={{ fontSize: 12 }}>days</span>
          {Number(targetDays) > freeDays && (
            <span style={{ fontSize: 11, color: C.gold }}>{allFlying ? '· grounds the force' : `· above ${freeDays}d wastes stock daily`}</span>
          )}
        </div>
      )}
      <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
        Caravans dispatch on their own when supply runs low (while the army holds still); a base on this tile is drawn from directly.
      </div>

      {armyBases.length > 0 && (
        <>
          <div style={h}>Send a caravan</div>
          <select style={input} value={dispatchBaseId ?? ''} onChange={e => setDispatchBaseId(Number(e.target.value))}>
            {armyBases.map(b => (
              <option key={b.id} value={b.id}>
                {baseLabel(b)} — {Math.round(b.stored_supply_gp).toLocaleString()} gp
              </option>
            ))}
          </select>
          {dispatchPreview && dispatchPreview.reachable === false && (
            <div style={{ fontSize: 12, color: C.gold, marginBottom: 4 }}>No land route from this base.</div>
          )}
          {dispatchPreview && dispatchPreview.reachable && (
            <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 4 }}>
              ~{dispatchPreview.travelDays}d travel · +{dispatchPreview.surchargePct}% surcharge — sending {Math.round(dispatchGp || 0).toLocaleString()} gp delivers ≈ <b>{Math.round((dispatchGp || 0) / dispatchPreview.costMult).toLocaleString()} gp</b>
              {dispatchPreview.targetMoving && <div style={{ color: C.gold }}>⚠ army has a move order — an equal-speed caravan can't catch a marching army</div>}
            </div>
          )}
          <div style={row}>
            <input style={{ ...input, flex: 1, marginBottom: 0 }} type="number" min={0} value={dispatchGp} onChange={e => setDispatchGp(Number(e.target.value))} />
            <button style={btn} disabled={!dispatchBaseId || !(dispatchGp > 0) || (dispatchPreview && dispatchPreview.reachable === false)}
              onClick={() => onDispatchCaravan(dispatchBaseId, selected.id, dispatchGp)}>Dispatch</button>
          </div>
        </>
      )}
    </>
    )
  }

  const renderOrders = () => (
    <>
      {!selected.commanded && !selected.leaderless_exempt && (
        <div style={{ fontSize: 12, color: C.gold, background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 6, padding: 8, marginBottom: 8 }}>
          ⚠ <b>No commander.</b> This army can only be ordered to march to a friendly stronghold (city or
          fort), or raid within 2 areas of a commanded friendly army. Leaderless for more than 2 days with
          other orders, it abandons them and marches home. Assign one in the <b>Commanders</b> tab.
          {selected.inbound_commander_name ? ` ${selected.inbound_commander_name} is riding in — arrives day ${selected.inbound_commander_day}.` : ''}
        </div>
      )}
      {!selected.commanded && !!selected.leaderless_exempt && (
        <div style={{ fontSize: 12, opacity: 0.7, background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 6, padding: 8, marginBottom: 8 }}>
          No commander, but the DM has exempted this army — it takes any order and keeps its orders.
        </div>
      )}
      {isDm && (
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <input type="checkbox" checked={!!selected.leaderless_exempt}
            onChange={e => onSetAutoResupply(selected.id, { leaderless_exempt: e.target.checked ? 1 : 0 })} />
          Exempt from the commander rule (DM) — may move without a leader
        </label>
      )}
      <div style={h}>Move order</div>
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>
        {mode === 'pathing'
          ? (plannedPath.length
              ? `Route: ${plannedPath.length} tile(s) · ~${Math.max(1, Math.ceil(plannedCost / (selected.movement_per_day || 2)))} day(s) — click again to extend or issue.`
              : 'Click a destination — the route is plotted for you. Click again to add waypoints.')
          : 'Select this army (click it) to plot a move.'}
      </div>
      {(() => {
        // catch under-provisioned marches at planning time, not on day 3 in the wilderness
        if (mode !== 'pathing' || !plannedPath.length || selected.carried_days == null) return null
        const routeDays = Math.max(1, Math.ceil(plannedCost / (selected.movement_per_day || 2)))
        if (effDays(selected) >= routeDays) return null
        return (
          <div style={{ fontSize: 12, color: C.gold, marginBottom: 6 }}>
            ⚠ This march takes ~{routeDays}d but the army carries {fmtD(effDays(selected))}d of supply{selected.half_rations ? ' (on ½ rations)' : ''}.
          </div>
        )
      })()}
      <div style={row}>
        <button style={btn} disabled={!plannedPath.length} onClick={onIssueOrder}>Issue order</button>
        <button style={btnGhost} disabled={!plannedPath.length} onClick={onClearPath}>Clear</button>
      </div>
      {selected.pendingOrder && selected.pendingOrder.order_type === 'move' && <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>Pending: {selected.pendingOrder.payload.path.length} tiles queued.</div>}

      <div style={h}>Raid {selected.tile_row != null ? (selected.tile_name || `(${selected.tile_row},${selected.tile_col})`) : ''}</div>
      <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6 }}>
        Standing order — raids the current tile each day. Forgoes moving. The men in the <b>Raiders</b> formation
        bucket do the raiding{(() => {
          const r = (selected.formation && selected.formation.totals && selected.formation.totals.raiders) || 0
          return r > 0 ? ` (currently ${r.toLocaleString()})` : ' — ordering with none assigned commits every unassigned man'
        })()}.
      </div>
      <div style={row}>
        <select style={{ ...input, flex: 2 }} value={stance} onChange={e => setStance(e.target.value)}>
          <option value="foraging">Foraging (food)</option>
          <option value="looting">Looting (wealth)</option>
          <option value="devastation">Devastation (ruin)</option>
        </select>
        <button style={btn} disabled={selected.tile_row == null} onClick={() => onRaid(selected.id, stance)}>Raid</button>
      </div>
      {selected.pendingOrder && selected.pendingOrder.order_type === 'raid' && (
        <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
          Raiding ({selected.pendingOrder.payload.stance}). <button style={{ ...btnGhost, padding: '2px 7px' }} onClick={() => onStandDown(selected.id)}>Stand down</button>
        </div>
      )}

      {renderDetachment()}
    </>
  )

  const renderDetachment = () => {
    const isDetached = selected.detachment_since_day != null
    const eligible = detachEligible(selected)
    const allFlying = selected.pools.length > 0 && selected.pools.every(p => p.disbanded || poolTags(p).includes('flying'))
    const dueDay = isDetached ? selected.detachment_since_day + CAMP_FOLLOWER_MAX_DAYS : null
    const overdue = isDetached && currentDay > dueDay
    const token = campFollowers.find(cf => cf.army_id === selected.id && !cf.fog)
    return (
      <>
        <div style={h}>Temporary detachment</div>
        {!eligible && !isDetached ? (
          <div style={{ fontSize: 11, opacity: 0.6 }}>Only an all-cavalry or all-flying force (no infantry) can travel light as a temporary detachment.</div>
        ) : (
          <>
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={isDetached} disabled={!isDetached && !eligible}
                onChange={e => onSetDetachment(selected.id, e.target.checked)} />
              Travel light (drop camp followers)
            </label>
            <div style={{ fontSize: 11, opacity: 0.6, margin: '2px 0 4px' }}>
              Marches 6 areas/day{allFlying ? ', ignoring terrain while airborne' : ''} and sheds the +20% camp-follower supply cost. Followers wait on the map where they’re left; rejoin at their tile or a friendly city. Unchecking reunites only where they’re reachable.
            </div>
            {isDetached && (
              <div style={{ fontSize: 12, background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 6, padding: 8 }}>
                <div style={{ color: overdue ? C.gold : C.ink }}>
                  {overdue
                    ? `⚠ Camp followers abandoned ${currentDay - selected.detachment_since_day} days (limit ${CAMP_FOLLOWER_MAX_DAYS}). Reunite them soon — the DM has been alerted.`
                    : `Camp followers required by day ${dueDay} (${dueDay - currentDay} day(s) left).`}
                </div>
                {token ? (
                  <div style={{ ...row, marginTop: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, opacity: 0.8, flex: 1 }}>Followers waiting at {token.tile_name || `(${token.tile_row},${token.tile_col})`}</span>
                    <button style={{ ...btnGhost, padding: '2px 8px' }} onClick={() => onDisperseFollowers(selected.id)}>Disperse</button>
                  </div>
                ) : (
                  <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6 }}>No followers waiting — they’ll re-form when this army reaches a friendly city.</div>
                )}
              </div>
            )}
          </>
        )}
      </>
    )
  }

  // parties assigned in the scouting config, short+long, per type
  const partiesAssigned = (army) => {
    let cfg = {}; try { cfg = JSON.parse(army.scouting_json || '{}') || {} } catch { cfg = {} }
    const P = cfg.parties || {}
    const r = {}
    for (const [tk] of SCOUT_TYPES) r[tk] = (Number((P.short || {})[tk]) || 0) + (Number((P.long || {})[tk]) || 0)
    return r
  }
  // formation warnings shown on the Formation tab (and echoed on Scouting for the scout case)
  const formationWarnings = (army) => {
    const warns = []
    const totals = (army.formation && army.formation.totals) || {}
    if (army.pendingOrder && army.pendingOrder.order_type === 'raid' && !(totals.raiders > 0))
      warns.push('⚠ This army has a raid order but no men in the Raiders bucket — the raid will do nothing.')
    const want = partiesAssigned(army)
    const inBucket = scoutBucketAvail(army)
    const whole = scoutAvail(army)
    for (const [tk, tlabel] of SCOUT_TYPES) {
      if (want[tk] > (inBucket[tk] || 0) && (whole[tk] || 0) > (inBucket[tk] || 0))
        warns.push(`⚠ ${want[tk]} ${tlabel} scout parties assigned but only ${inBucket[tk] || 0} capable men in the Scouts bucket — move more into Scouts or the parties won't field.`)
    }
    return warns
  }

  const renderFormation = () => {
    const totals = (selected.formation && selected.formation.totals) || { main: 0 }
    const warns = formationWarnings(selected)
    return (
      <>
        <div style={h}>Marching formation</div>
        <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6 }}>
          Everyone starts in the main body, guarding the carts. Scouts feed scout parties; Raiders are the
          men who raid; Supply-line guards ride inbound caravans. Vanguard and rear guard matter when
          battles arrive.
        </div>
        {warns.map((w, i) => <div key={i} style={{ fontSize: 12, color: C.gold, marginBottom: 4 }}>{w}</div>)}
        <div style={{ background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 6, padding: 8, marginBottom: 8 }}>
          {FORMATION_ORDER.map(([bk, blabel]) => (
            <div key={bk} style={{ display: 'flex', fontSize: 12.5, padding: '2px 0', opacity: (totals[bk] || 0) > 0 ? 1 : 0.45 }}>
              <span style={{ flex: 1 }}>{blabel}</span>
              <b>{(totals[bk] || 0).toLocaleString()}</b>
            </div>
          ))}
        </div>
        <button style={{ ...btn, width: '100%' }} onClick={() => setFormationOpen(true)}>Arrange formation…</button>
      </>
    )
  }

  const renderScouting = () => (
    <>
      <div style={h}>Scouting</div>
      <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6 }}>Send scout parties to detect enemies. Power needs scout-capable troops; setting any range ≥24mi locks both ranges for 2 days.</div>
      {scoutForm.rangeLockedUntilDay > currentDay && <div style={{ fontSize: 11, color: C.gold, marginBottom: 4 }}>Ranges locked until day {scoutForm.rangeLockedUntilDay}.</div>}
      {[['short', 'Short'], ['long', 'Long']].map(([rk, rlabel]) => (
        <div key={rk} style={{ marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <span style={{ fontSize: 12, width: 42 }}>{rlabel}</span>
            <input style={{ ...input, flex: 1, marginBottom: 0 }} type="number" min={0} max={60} step={6}
              value={scoutForm[rk + 'RangeMi']} onChange={e => setScoutForm(f => ({ ...f, [rk + 'RangeMi']: Number(e.target.value) }))} />
            <span style={{ fontSize: 11, opacity: 0.6 }}>mi</span>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {SCOUT_TYPES.map(([tk, tlabel]) => (
              <label key={tk} style={{ flex: 1, fontSize: 10, opacity: 0.8, textAlign: 'center' }}>
                {tlabel}
                <input style={{ ...input, marginBottom: 0, padding: 2, textAlign: 'center' }} type="number" min={0}
                  value={scoutForm.parties[rk][tk]}
                  onChange={e => setScoutForm(f => ({ ...f, parties: { ...f.parties, [rk]: { ...f.parties[rk], [tk]: Number(e.target.value) } } }))} />
              </label>
            ))}
          </div>
        </div>
      ))}
      <div style={{ fontSize: 10, opacity: 0.6, marginBottom: 2 }}>Scout-capable men: {SCOUT_TYPES.map(([tk, tl]) => `${tl} ${scoutAvail(selected)[tk]}`).join(' · ')}</div>
      <div style={{ fontSize: 10, opacity: 0.6, marginBottom: 6 }}>In the Scouts bucket (parties draw from these — see Formation): {SCOUT_TYPES.map(([tk, tl]) => `${tl} ${scoutBucketAvail(selected)[tk]}`).join(' · ')}</div>
      {formationWarnings(selected).filter(w => w.includes('Scouts bucket')).map((w, i) => (
        <div key={i} style={{ fontSize: 11, color: C.gold, marginBottom: 4 }}>{w}</div>
      ))}
      <div style={row}>
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}><input type="checkbox" checked={scoutForm.halfSpeed} onChange={e => setScoutForm(f => ({ ...f, halfSpeed: e.target.checked }))} /> Half-speed</label>
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}><input type="checkbox" checked={scoutForm.localGuide} onChange={e => setScoutForm(f => ({ ...f, localGuide: e.target.checked }))} /> Local guide</label>
      </div>
      <button style={{ ...btn, width: '100%', marginTop: 6 }} onClick={() => onSetScouting(selected.id, scoutForm)}>Save scouting orders</button>
    </>
  )

  // army-context header: name, back-to-armies, compact meta
  const menCount = armyContext ? selected.pools.reduce((s, p) => s + (p.disbanded ? 0 : p.men), 0) : 0
  const orderText = armyContext && selected.pendingOrder
    ? (selected.pendingOrder.order_type === 'raid' ? ` · raiding (${selected.pendingOrder.payload.stance})`
      : selected.pendingOrder.order_type === 'move' ? ` · moving (${selected.pendingOrder.payload.path.length} left)` : '')
    : ''

  const tabs = armyContext ? armyTabs : worldTabs
  const active = armyContext ? activeArmy : activeWorld
  const onPick = armyContext ? setArmyTab : setWorldTab

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <div style={{ padding: '12px 14px 10px', borderBottom: `1px solid ${C.rule}` }}>
        {armyContext && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <button style={{ ...btnGhost, padding: '3px 9px' }} onClick={onDeselect} title="Back to armies">←</button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.name}</b>
              <div style={{ fontSize: 11, opacity: 0.7 }}>
                {menCount} men · {selected.faction_name}
                {selected.commander_name ? ` · ${selected.commander_name}` : ''}
                {` · ${placeOf(selected)}`}
                {selected.carried_days != null ? ` · ${fmtD(selected.carried_days)}d supply` : ''}{orderText}
                {!selected.commanded && !selected.leaderless_exempt && ownSelected ? <span style={{ color: C.gold }}> · ⚠ no commander</span> : ''}
              </div>
            </div>
          </div>
        )}
        <TabBar tabs={tabs} active={active} onPick={onPick} />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 14px 16px' }}>
        {armyContext ? (
          <>
            {active === 'roster' && renderRoster()}
            {active === 'supply' && renderSupply()}
            {active === 'orders' && renderOrders()}
            {active === 'siege' && (
              <SiegePanel isDm={isDm} canAct={canAct} myFactionId={myFactionId} army={selected}
                strongholds={strongholds} sieges={sieges} currentDay={currentDay}
                onBesiege={onBesiege} onLift={onLiftSiege} onAssault={onAssault} onSally={onSally}
                onTerms={onSiegeTerms} onResolve={onResolveSiege} onSurrenderCheck={onSurrenderCheck}
                onAllocate={onSiegeAllocation} onSpecialists={onSpecialists} />
            )}
            {active === 'formation' && renderFormation()}
            {active === 'scouting' && renderScouting()}
          </>
        ) : (
          <>
            {selected && selected.fog && renderFoggedCard()}
            {active === 'armies' && renderArmiesList()}
            {active === 'commanders' && (
              <CommandersPanel isDm={isDm} canAct={canAct} factions={factions} myFactionId={myFactionId}
                armies={armies} data={commandersData}
                onCreate={onCreateCommander} onPatch={onPatchCommander} onDelete={onDeleteCommander} />
            )}
            {active === 'logistics' && renderLogistics()}
            {active === 'strongholds' && (
              <SiegePanel worldContext isDm={isDm} canAct={canAct} myFactionId={myFactionId}
                strongholds={strongholds} sieges={sieges} currentDay={currentDay}
                onPatchStronghold={onPatchStronghold} onSeedStrongholds={onSeedStrongholds} />
            )}
            {active === 'day' && renderDay()}
            {active === 'campaign' && renderCampaign()}
          </>
        )}
      </div>

      {formationOpen && ownSelected && onSetFormation && (
        <FormationModal army={selected}
          onSave={(assignments) => onSetFormation(selected.id, assignments)}
          onClose={() => setFormationOpen(false)} />
      )}
    </div>
  )
}
